import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ChevronRight, CalendarHeart, Tags, Handshake, Star, Quote,
  Sparkles, ArrowRight, Crown, MessageCircle,
} from "lucide-react";
import SearchBar from "../components/SearchBar";
import VenueCard from "../components/VenueCard";
import Header from "../components/Header";
import { Seo, orgJsonLd, websiteJsonLd } from "../lib/seo";
import Footer from "../components/Footer";
import { FreeToolsSection } from "../components/FreeToolsSection";
import {
  HERO_SLIDES, POPULAR_CATEGORY_SLUGS,
  getFeaturedVenues, STEPS, TESTIMONIALS, VENUES,
} from "../mock/mockData";
import { useCategories } from "../context/CategoriesContext";
import { useMember } from "../context/MemberContext";

const stepIcons = { CalendarHeart, Tags, Handshake };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const { categories: CATEGORIES, getCategory } = useCategories();
  const { member } = useMember();
  const [cityVenues, setCityVenues] = useState([]);
  const [slide, setSlide] = useState(0);
  const [sponsored, setSponsored] = useState([]);
  const [articles, setArticles] = useState([]);
  const [advisorUrl, setAdvisorUrl] = useState("");
  const featured = getFeaturedVenues();
  const popularCats = POPULAR_CATEGORY_SLUGS.map(getCategory).filter(Boolean);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    axios.get(`${API}/firms-featured`, { params: { limit: 8 } })
      .then((r) => setSponsored(r.data || []))
      .catch(() => setSponsored([]));
  }, []);

  useEffect(() => {
    axios.get(`${API}/articles`)
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]));
  }, []);

  useEffect(() => {
    axios.get(`${API}/advisor`)
      .then((r) => setAdvisorUrl(r.data?.whatsappUrl || ""))
      .catch(() => setAdvisorUrl(""));
  }, []);

  useEffect(() => {
    const city = member?.city;
    if (!city) { setCityVenues([]); return; }
    axios.get(`${API}/firms/search`, { params: { city, limit: 8 } })
      .then((r) => {
        const backend = (r.data?.items || []).filter((v) => v.city === city);
        const mock = VENUES.filter((v) => v.city === city);
        const map = {};
        [...backend, ...mock].forEach((v) => { if (v.slug && !map[v.slug]) map[v.slug] = v; });
        setCityVenues(Object.values(map).slice(0, 8));
      })
      .catch(() => {
        setCityVenues(VENUES.filter((v) => v.city === city).slice(0, 8));
      });
  }, [member]);

  return (
    <div>
      <Seo
        title="DüğünSenin | Düğün Mekanları ve Düğün Firmaları Rehberi"
        description="Türkiye'nin düğün mekanları ve düğün firmaları rehberi. Şehrindeki düğün salonları, kır düğünü, otel düğünleri ve organizasyon firmalarını keşfet, ücretsiz teklif al."
        canonical="/"
        jsonLd={[orgJsonLd(), websiteJsonLd()]}
      />
      <Header />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center justify-center overflow-hidden">
        {HERO_SLIDES.map((src, i) => (
          <div
            key={src}
            className={`absolute inset-0 transition-opacity ease-in-out ${
              i === slide ? "opacity-100" : "opacity-0"
            }`}
            style={{ transitionDuration: "1400ms" }}
          >
            <img
              src={src}
              alt="Dugun"
              className={`w-full h-full object-cover ${i === slide ? "animate-kenburns" : ""}`}
            />
          </div>
        ))}
        <div className="absolute inset-0 hero-overlay" />

        <div className="relative z-10 max-w-4xl mx-auto px-5 text-center pt-24 pb-16">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#E3CE9E] via-[#C6A15B] to-[#a9843f] text-[#2A1C0B] text-sm font-bold tracking-wide mb-6 shadow-[0_6px_24px_rgba(198,161,91,0.55)] ring-1 ring-white/40 animate-fade-in uppercase">
            <Sparkles className="w-4 h-4 text-[#7a5a12] animate-pulse" fill="currentColor" />
            Türkiye'nin En Büyük Düğün Rehberi
            <Sparkles className="w-4 h-4 text-[#7a5a12] animate-pulse" fill="currentColor" />
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-bold text-white leading-tight text-shadow-soft animate-fade-up">
            Hayalindeki Düğün İçin<br />
            <span className="text-[#E3CE9E] italic">En Doğru Adres</span>
          </h1>
          <p className="mt-5 text-lg text-white/90 max-w-2xl mx-auto text-shadow-soft animate-fade-up delay-100">
            Düğün mekanları, organizasyon firmaları ve daha fazlası... Aradığın her şey burada.
          </p>

          <div className="mt-10 animate-fade-up delay-200">
            <SearchBar variant="hero" />
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-white/85 text-sm animate-fade-up delay-300">
            <span className="flex items-center gap-2"><Star className="w-4 h-4 text-[#E3CE9E]" fill="#E3CE9E" /> 4.8/5 memnuniyet</span>
            <span className="flex items-center gap-2"><CalendarHeart className="w-4 h-4 text-[#E3CE9E]" /> 20000+ firma</span>
            <span className="flex items-center gap-2"><Handshake className="w-4 h-4 text-[#E3CE9E]" /> Ücretsiz danışmanlık</span>
          </div>
        </div>
      </section>

      <FreeToolsSection />

      {/* MEMBER CITY VENUES */}
      {member?.city && cityVenues.length > 0 && (
      <section className="max-w-7xl mx-auto px-5 pt-16" data-testid="member-city-section">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <span className="text-sm tracking-[0.2em] text-[#C6A15B] uppercase font-medium">Size Özel</span>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#2A2224] mt-2">{member.city}'daki Firmalar</h2>
            <p className="text-[#7C6E70] mt-2">Şehrinizdeki düğün firma ve mekanları</p>
          </div>
          <Link to={`/kategori/dugun-salonlari?sehir=${encodeURIComponent(member.city)}`} className="flex items-center gap-1.5 text-[#9E2A3B] font-medium hover:gap-2.5 transition-all">
            Tümünü Gör <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cityVenues.map((v) => <VenueCard key={v.slug || v.id} venue={v} />)}
        </div>
      </section>
      )}

      {/* POPULAR CATEGORIES */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="text-center mb-12">
          <span className="text-sm tracking-[0.2em] text-[#C6A15B] uppercase font-medium">Kategoriler</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#2A2224] mt-2">Popüler Kategoriler</h2>
          <p className="text-[#7C6E70] mt-3">En çok ziyaret edilen düğün hizmetleri</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {popularCats.map((c, i) => (
            <Link
              key={c.slug}
              to={`/kategori/${c.slug}`}
              className="card-hover group relative rounded-2xl overflow-hidden h-44 animate-fade-up"
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              <div className="img-zoom absolute inset-0">
                <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#9E2A3B]/85 via-black/25 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <h3 className="text-white font-medium text-sm leading-snug">{c.name}</h3>
                <span className="flex items-center gap-1 text-[#E3CE9E] text-xs mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Keşfet <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* SPONSORED / ÖNE ÇIKAN İŞLETMELER (paid) */}
      {sponsored.length > 0 && (
        <section className="bg-gradient-to-b from-[#FBF5F0] to-[#F6E4E6]/40 py-20">
          <div className="max-w-7xl mx-auto px-5">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <div>
                <span className="inline-flex items-center gap-1.5 text-sm tracking-[0.2em] text-[#C6A15B] uppercase font-semibold">
                  <Crown className="w-4 h-4" /> Sponsorlu
                </span>
                <h2 className="font-display text-3xl md:text-4xl font-bold text-[#2A2224] mt-2">Öne Çıkan İşletmeler</h2>
                <p className="text-[#7C6E70] mt-2">Bu hafta öne çıkan, seçkin düğün işletmeleri</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {sponsored.map((v, i) => (
                <div key={v.slug} className="relative">
                  <span className="absolute -top-2 left-4 z-10 flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-[#C6A15B] to-[#a9843f] text-white text-xs font-semibold shadow-md">
                    <Crown className="w-3 h-3" /> Öne Çıkan
                  </span>
                  <VenueCard venue={v} index={i} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED VENUES */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-5">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <span className="text-sm tracking-[0.2em] text-[#C6A15B] uppercase font-medium">Seçkin Mekanlar</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-[#2A2224] mt-2">Popüler Mekanlar</h2>
            </div>
            <Link to="/kategori/dugun-salonlari" className="flex items-center gap-2 text-[#9E2A3B] font-medium link-underline">
              Tümünü Gör <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.slice(0, 8).map((v, i) => (
              <VenueCard key={v.id} venue={v} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="text-center mb-14">
          <span className="text-sm tracking-[0.2em] text-[#C6A15B] uppercase font-medium">Kolay & Hızlı</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#2A2224] mt-2">DüğünSenin.com Nasıl Çalışır?</h2>
          <p className="text-[#7C6E70] mt-3 max-w-2xl mx-auto">Üç basit adımda hayalinizdeki düğüne ulaşın</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
          {STEPS.map((s, i) => {
            const Icon = stepIcons[s.icon];
            return (
              <div key={s.title} className="relative text-center animate-fade-up" style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="relative mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-[#F6E4E6] to-[#F3D9DC] flex items-center justify-center mb-5 shadow-sm">
                  <Icon className="w-9 h-9 text-[#9E2A3B]" />
                  <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#C6A15B] text-white text-sm font-bold flex items-center justify-center shadow">
                    {i + 1}
                  </span>
                </div>
                <h3 className="font-display text-xl font-semibold text-[#2A2224] mb-2">{s.title}</h3>
                <p className="text-[#7C6E70] text-sm leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ALL CATEGORIES STRIP */}
      <section className="bg-[#F6E4E6]/50 py-16">
        <div className="max-w-7xl mx-auto px-5">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#2A2224] text-center mb-8">Tüm Kategoriler</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to={`/kategori/${c.slug}`}
                className="px-4 py-2 rounded-full bg-white text-[#5b4d4f] text-sm border border-[#F0DDE0] hover:bg-[#9E2A3B] hover:text-white hover:border-[#9E2A3B] transition-colors"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-5 py-20">
        <div className="text-center mb-12">
          <span className="text-sm tracking-[0.2em] text-[#C6A15B] uppercase font-medium">Mutlu Çiftler</span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#2A2224] mt-2">Onlar Düğünlerini Bizimle Planladı</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} className="bg-white rounded-2xl p-7 border border-[#F0DDE0] card-hover animate-fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
              <Quote className="w-8 h-8 text-[#E7A9B0] mb-3" />
              <p className="text-[#5b4d4f] leading-relaxed italic">"{t.text}"</p>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-[#F0DDE0]">
                <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover" />
                <div>
                  <p className="font-medium text-[#2A2224] text-sm">{t.name}</p>
                  <p className="text-xs text-[#7C6E70]">{t.city}</p>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 text-[#C6A15B]" fill="#C6A15B" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 pb-20">
        <div className="relative rounded-3xl overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1519225421980-715cb0215aed?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Düğün"
            className="w-full h-full object-cover absolute inset-0"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#9E2A3B]/90 to-[#9E2A3B]/60" />
          <div className="relative z-10 px-8 md:px-16 py-16 text-center md:text-left">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white max-w-xl">
              Ücretsiz Organizasyon Danışmanlığı
            </h2>
            <p className="text-white/90 mt-4 max-w-xl">
              Sana özel danışmanımızla en uygun firmalarla anlaş, düğün stresini bize bırak.
            </p>
            {advisorUrl ? (
              <a
                href={`${advisorUrl}${advisorUrl.includes("?") ? "&" : "?"}text=${encodeURIComponent("Merhaba, dugunsenin.com üzerinden ücretsiz organizasyon danışmanlığı almak istiyorum.")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-advisor-whatsapp"
                className="inline-flex items-center gap-2 mt-7 px-8 py-3.5 rounded-full bg-[#25D366] text-white font-semibold shadow-lg hover:-translate-y-0.5 transition-transform"
              >
                <MessageCircle className="w-5 h-5" /> Hemen Başla
              </a>
            ) : (
              <Link
                to="/kategori/organizasyon-firmalari"
                data-testid="home-advisor-fallback"
                className="inline-flex items-center gap-2 mt-7 px-8 py-3.5 rounded-full bg-white text-[#9E2A3B] font-semibold shadow-lg hover:-translate-y-0.5 transition-transform"
              >
                Hemen Başla <ArrowRight className="w-5 h-5" />
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* REHBER YAZILAR */}
      {articles.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 pb-20" data-testid="home-articles">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-[#2A2224]">Rehber Yazılar</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {articles.slice(0, 8).map((a) => {
              const Card = (
                <div className="group relative rounded-2xl overflow-hidden h-[320px] shadow-[0_20px_45px_-25px_rgba(158,42,59,0.35)] border border-[#F0DDE0] bg-white">
                  {a.image ? (
                    <img src={a.image} alt={a.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-[#F6E4E6] to-[#FBF5F0]" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-lg font-semibold text-white leading-snug">{a.title}</h3>
                    {a.excerpt && <p className="text-white/85 text-sm mt-1.5 line-clamp-2">{a.excerpt}</p>}
                  </div>
                </div>
              );
              return a.linkUrl ? (
                <a key={a.id} href={a.linkUrl} target={/^https?:\/\//.test(a.linkUrl) ? "_blank" : undefined} rel="noopener noreferrer" data-testid={`article-card-${a.id}`}>{Card}</a>
              ) : a.slug ? (
                <Link key={a.id} to={`/rehber/${a.slug}`} data-testid={`article-card-${a.id}`}>{Card}</Link>
              ) : (
                <div key={a.id} data-testid={`article-card-${a.id}`}>{Card}</div>
              );
            })}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Home;
